import type { ReactNode } from 'react';
import { Archive, ChevronRight, MoreVertical } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

export interface EditorBreadcrumbItem {
	label: string;
	/** Click handler for a navigable crumb. The last crumb (current page) omits this and renders muted. */
	onClick?: () => void;
}

interface EditorHeaderProps {
	breadcrumb: EditorBreadcrumbItem[];
	title: string;
	/** Status badges shown under the title (e.g. <Pill> elements). */
	pills?: ReactNode;
	/** Extra items rendered above Delete in the ⋮ menu (e.g. "Set as hero"). */
	menuItems?: ReactNode;
	/** Delete handler — when omitted (and no menuItems), the ⋮ menu is hidden. */
	onDelete?: () => void;
	deleteLabel?: string;
	deleting?: boolean;
	onCancel: () => void;
	saving?: boolean;
	isDirty?: boolean;
}

/**
 * Full-page editor header card: breadcrumb → title + status pills → actions
 * (⋮ menu, Cancel, Save with dirty indicator). The Save button is `type="submit"`,
 * so the header must render inside the editor's <form>. Shared by every full-page
 * editor to keep the chrome consistent (mirrors the News/Banner editor head).
 */
export function EditorHeader({
	breadcrumb,
	title,
	pills,
	menuItems,
	onDelete,
	deleteLabel,
	deleting,
	onCancel,
	saving,
	isDirty
}: EditorHeaderProps) {
	const { t } = useTranslation();
	const hasMenu = Boolean(menuItems) || Boolean(onDelete);

	return (
		<div className="rounded-lg border border-border bg-surface p-4 px-5">
			<nav className="mb-3 flex flex-wrap items-center gap-1 text-[11px] text-foreground-subtle">
				{breadcrumb.map((item, i) => {
					const isLast = i === breadcrumb.length - 1;
					return (
						<span key={i} className="flex items-center gap-1">
							{i > 0 && <ChevronRight size={12} />}
							{isLast ? (
								<span className="text-foreground-muted">{item.label}</span>
							) : item.onClick ? (
								<button type="button" onClick={item.onClick} className="hover:text-foreground">
									{item.label}
								</button>
							) : (
								<span>{item.label}</span>
							)}
						</span>
					);
				})}
			</nav>

			<div className="grid grid-cols-[1fr_auto] items-start gap-4">
				<div className="min-w-0">
					<h1 className="line-clamp-2 text-2xl font-semibold tracking-[-0.015em] text-foreground">{title}</h1>
					{pills && <div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-foreground-muted">{pills}</div>}
				</div>
				<div className="flex items-center gap-2">
					{hasMenu && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button variant="outline" size="icon" type="button" aria-label={t('common.sections')}>
									<MoreVertical size={16} />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{menuItems}
								{onDelete && (
									<DropdownMenuItem variant="destructive" onSelect={onDelete} disabled={deleting}>
										<Archive size={14} /> {deleteLabel ?? t('common.delete')}
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}
					<Button type="button" variant="ghost" onClick={onCancel}>
						{t('common.cancel')}
					</Button>
					<Button type="submit" className="relative" disabled={saving}>
						{saving ? t('common.saving') : t('common.save')}
						{isDirty && !saving && <span className="absolute -right-1 -top-1 h-[7px] w-[7px] rounded-full bg-amber-500" />}
					</Button>
				</div>
			</div>
		</div>
	);
}
